import React, { useEffect, useState } from 'react';
import { App, Input, Table, Select, Checkbox, Button, Spin, Alert } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { PageTitle, StyledCard } from '../common';
import { theme } from '../../theme';

const { Option } = Select;

interface TicketEditable {
  id: number;
  numero_ticket: number;
  numero_remito: string;
  dtv: string;
  kilos_netos: number;
  fecha: string;
  precio: number;
  total: number;
}

interface RetencionEditable {
  id: number;
  retencion_id: number;
  descripcion: string;
  tipo: string;
  provincia_nombre: string | null;
  porcentaje: number;
  portonelada: number;
  acumulado_ganancia: number;
  importe: number;
  seleccionada: boolean;
}

const EditarCompraAlgodon: React.FC = () => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { getAccessTokenSilently, user } = useAuth0();

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [asientoContableId, setAsientoContableId] = useState<number | null>(null);

  const [clienteNombre, setClienteNombre] = useState('');
  const [tickets, setTickets] = useState<TicketEditable[]>([]);
  const [retenciones, setRetenciones] = useState<RetencionEditable[]>([]);
  const [ivaPorcentaje, setIvaPorcentaje] = useState('10.5');
  const [acumuladoGanancias, setAcumuladoGanancias] = useState(0);
  const [nroCompra, setNroCompra] = useState<number | null>(null);

  useEffect(() => {
    if (user && user.tipo_usuario !== 'D') {
      navigate('/listar_compraalgodon');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.id_empresa && id) {
      cargarDatos();
    }
  }, [user, id]);

  const cargarDatos = async () => {
    setCargando(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [liqRes, retAllRes] = await Promise.all([
        axios.get(
          `${process.env.REACT_APP_LIQUIDACION_URL}/obtener_liquidacion_compraalgodon/${user?.id_empresa}/${user?.id_sucursal}/${id}/`,
          { headers }
        ),
        axios.get(
          `${process.env.REACT_APP_LIQUIDACION_URL}/listar_retenciones/${user?.id_empresa}/`,
          { headers }
        ),
      ]);

      const liq = liqRes.data.liquidacion;
      setClienteNombre(`${liq.cliente_nc} - ${liq.cliente_nombre}`);
      setNroCompra(liq.nro_compra);
      setAsientoContableId(liq.asiento_contable_id ?? null);

      const ticketsExistentes: TicketEditable[] = liqRes.data.tickets.map((t: any) => ({
        id: t.id,
        numero_ticket: t.numero_ticket,
        numero_remito: t.ticket_balanza?.numero_remito ?? '',
        dtv: t.ticket_balanza?.dtv ?? '',
        kilos_netos: t.cantidad,
        fecha: t.ticket_balanza?.fecha_ticket ?? '',
        precio: t.precio,
        total: t.total,
      }));
      setTickets(ticketsExistentes);

      // Calcular IVA % desde los datos guardados
      const subtotal = parseFloat(liq.subtotal) || 0;
      const iva = parseFloat(liq.iva) || 0;
      if (subtotal > 0) {
        const pct = (iva / subtotal) * 100;
        if (Math.abs(pct - 21) < 0.5) setIvaPorcentaje('21');
        else if (Math.abs(pct - 10.5) < 0.5) setIvaPorcentaje('10.5');
        else setIvaPorcentaje('0.00');
      }

      // Retenciones existentes en la liquidación
      const retExistentesIds = new Set<number>(liqRes.data.retenciones.map((r: any) => r.retencion_info.id));

      // Todas las retenciones disponibles para la empresa
      const retAll = (retAllRes.data.results ?? retAllRes.data).filter(
        (r: any) => r.alta === 1 && r.aplicar !== 'SERVICIO'
      );

      const retEditables: RetencionEditable[] = retAll.map((r: any) => ({
        id: retExistentesIds.has(r.id)
          ? liqRes.data.retenciones.find((re: any) => re.retencion_info.id === r.id)?.id ?? 0
          : 0,
        retencion_id: r.id,
        descripcion: r.descripcion,
        tipo: r.tipo,
        provincia_nombre: r.provincia_nombre ?? null,
        porcentaje: parseFloat(r.porcentaje),
        portonelada: parseFloat(r.portonelada),
        acumulado_ganancia: parseFloat(r.acumulado_ganancia),
        importe: retExistentesIds.has(r.id)
          ? parseFloat(liqRes.data.retenciones.find((re: any) => re.retencion_info.id === r.id)?.importe ?? '0')
          : 0,
        seleccionada: retExistentesIds.has(r.id),
      }));
      setRetenciones(retEditables);

      // Acumulado ganancias
      if (liq.cliente) {
        const acumRes = await axios.get(
          `${process.env.REACT_APP_LIQUIDACION_URL}/acumulado_ganancias/${user?.id_empresa}/${user?.id_sucursal}/${liq.cliente}/`,
          { headers }
        );
        setAcumuladoGanancias(acumRes.data.acumulado_subtotal ?? 0);
      }
    } catch (e: any) {
      setError('Error al cargar la liquidación');
    } finally {
      setCargando(false);
    }
  };

  const handlePrecioChange = (ticketId: number, precio: number) => {
    setTickets(prev => prev.map(t => {
      if (t.id !== ticketId) return t;
      const total = (t.kilos_netos / 1000) * precio;
      return { ...t, precio, total };
    }));
  };

  const handleRetencionSelect = (retencionId: number, checked: boolean) => {
    setRetenciones(prev => prev.map(r =>
      r.retencion_id === retencionId ? { ...r, seleccionada: checked } : r
    ));
  };

  const calcularImporteGanancias = (ret: RetencionEditable): number => {
    const A1 = acumuladoGanancias;
    const A2 = totalSubtotal;
    const A3 = ret.acumulado_ganancia;
    let IM = 0;
    if (A1 > A3) IM = A2;
    else if ((A1 + A2) > A3) IM = A1 + A2 - A3;
    return (IM * ret.porcentaje) / 100;
  };

  const calcularImporte = (ret: RetencionEditable): number => {
    if (!ret.seleccionada) return 0;
    if (ret.tipo === 'RENTAS') return (totalKilos * ret.portonelada) / 1000;
    if (ret.tipo === 'GANANCIAS' || ret.tipo === 'GANANCIA') return calcularImporteGanancias(ret);
    if (ret.tipo === 'IVA') return (totalSubtotal * ret.porcentaje) / 100;
    return ((totalSubtotal * ret.porcentaje) / 100 + totalKilos * ret.portonelada) / 1000;
  };

  const totalKilos = tickets.reduce((s, t) => t.precio !== 0 ? s + t.kilos_netos : s, 0);
  const totalSubtotal = tickets.reduce((s, t) => s + t.total, 0);
  const ivaImporte = (totalSubtotal * parseFloat(ivaPorcentaje)) / 100;
  const totalRetenciones = retenciones.reduce((s, r) => s + calcularImporte(r), 0);
  const totalGeneral = totalSubtotal + ivaImporte - totalRetenciones;

  const fmt = (v: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(v);

  const handleAnularCompra = () => {
    modal.confirm({
      title: `¿Anular Liquidación Compra N° ${nroCompra}?`,
      content: 'Esta acción marcará la liquidación como anulada y liberará los tickets asociados. No se puede deshacer.',
      okText: 'Anular',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        setAnulando(true);
        try {
          const token = await getAccessTokenSilently();
          await axios.put(
            `${process.env.REACT_APP_LIQUIDACION_URL}/anular_liquidacion_compra/${user?.id_empresa}/${user?.id_sucursal}/${id}/`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(`Liquidación N° ${nroCompra} anulada`);
          navigate('/listar_compraalgodon');
        } catch (err: any) {
          message.error(err?.response?.data?.error ?? 'Error al anular la liquidación');
        } finally {
          setAnulando(false);
        }
      },
    });
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.put(
        `${process.env.REACT_APP_LIQUIDACION_URL}/editar_liquidacion_compra/${user?.id_empresa}/${user?.id_sucursal}/${id}/`,
        {
          tickets: tickets.map(t => ({ id: t.id, precio: t.precio, total: t.total })),
          retenciones: retenciones.map(r => ({
            id: r.retencion_id,
            portonelada: r.portonelada,
            porcentaje: r.porcentaje,
            importe: calcularImporte(r),
            seleccionada: r.seleccionada,
          })),
          subtotal: totalSubtotal,
          iva: ivaImporte,
          total_retenciones: totalRetenciones,
          total_general: totalGeneral,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Liquidación actualizada correctamente');
      navigate('/listar_compraalgodon');
    } catch (e: any) {
      message.error(e?.response?.data?.error ?? 'Error al guardar los cambios');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  if (error) {
    return <Alert type="error" message={error} style={{ margin: 24 }} />;
  }

  return (
    <div style={{ padding: '0' }}>
      <PageTitle style={{ marginBottom: '12px', fontSize: '24px' }}>
        Editar Liquidación de Compra N° {nroCompra}
      </PageTitle>

      <StyledCard title="Información de Liquidación" bodyStyle={{ padding: '12px' }}>
        <style>{`
          .edit-liq-table .ant-table { font-size: 12px; }
          .edit-liq-table .ant-table-thead > tr > th { font-size: 12px; padding: 6px 8px; font-weight: 600; }
          .edit-liq-table .ant-table-tbody > tr > td { font-size: 12px; padding: 6px 8px; }
        `}</style>

        <div style={{ marginBottom: 12, padding: '6px 10px', backgroundColor: '#e6f7ff', borderRadius: 6, border: '1px solid #91d5ff', fontSize: 13 }}>
          <strong>Cliente:</strong> {clienteNombre}
        </div>

        <StyledCard title="Tickets" style={{ marginTop: 12 }} bodyStyle={{ padding: 12 }}>
          <Table
            className="edit-liq-table"
            dataSource={tickets}
            rowKey="id"
            pagination={false}
            scroll={{ y: 200 }}
            size="small"
            bordered
            columns={[
              { title: 'N° Ticket', dataIndex: 'numero_ticket', width: 90 },
              { title: 'N° Remito', dataIndex: 'numero_remito', width: 110 },
              { title: 'DTV', dataIndex: 'dtv', width: 110 },
              {
                title: 'Kilos Netos', dataIndex: 'kilos_netos', width: 110, align: 'right',
                render: (v) => new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2 }).format(v),
              },
              { title: 'Fecha', dataIndex: 'fecha', width: 100 },
              {
                title: '$/Tn', dataIndex: 'precio', width: 110, align: 'right',
                render: (value, record) => (
                  <Input
                    type="number"
                    step="0.01"
                    value={value}
                    onChange={(e) => handlePrecioChange(record.id, parseFloat(e.target.value) || 0)}
                    style={{ textAlign: 'right', width: '100%', fontSize: 12, height: 28 }}
                  />
                ),
              },
              {
                title: 'Subtotal', dataIndex: 'total', width: 120, align: 'right',
                render: (v) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(v || 0),
              },
            ]}
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  <Table.Summary.Cell index={0} colSpan={3} />
                  <Table.Summary.Cell index={3} align="right">
                    {new Intl.NumberFormat('es-AR', { minimumFractionDigits: 2 }).format(totalKilos)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>IVA:</span>
                      <Select value={ivaPorcentaje} onChange={setIvaPorcentaje} style={{ width: 70 }} size="small">
                        <Option value="0.00">0.00</Option>
                        <Option value="10.5">10.5</Option>
                        <Option value="21">21</Option>
                      </Select>
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">{fmt(totalSubtotal)}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        </StyledCard>

        {retenciones.length > 0 && (
          <StyledCard title="Retenciones" style={{ marginTop: 12 }} bodyStyle={{ padding: 12 }}>
            <Table
              className="edit-liq-table"
              dataSource={retenciones}
              rowKey="retencion_id"
              pagination={false}
              scroll={{ y: 150 }}
              size="small"
              bordered
              columns={[
                {
                  title: '', key: 'sel', width: 40, align: 'center',
                  render: (_, r) => (
                    <Checkbox checked={r.seleccionada} onChange={(e) => handleRetencionSelect(r.retencion_id, e.target.checked)} />
                  ),
                },
                { title: 'Descripción', dataIndex: 'descripcion', width: 200 },
                { title: 'Tipo', dataIndex: 'tipo', width: 90 },
                { title: 'Provincia', dataIndex: 'provincia_nombre', width: 130, render: (v) => v ?? '-' },
                { title: '%', dataIndex: 'porcentaje', width: 70, align: 'right', render: (v) => `${v}%` },
                {
                  title: 'Importe', key: 'importe', width: 120, align: 'right',
                  render: (_, r) => fmt(calcularImporte(r)),
                },
              ]}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8, fontWeight: 600, fontSize: 13 }}>
              <span>Total Retenciones: <span style={{ color: theme.colors.primary }}>{fmt(totalRetenciones)}</span></span>
            </div>
          </StyledCard>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, padding: '8px 12px', backgroundColor: '#e6f7ff', borderRadius: 6, border: `2px solid ${theme.colors.primary}`, fontWeight: 600, fontSize: 14 }}>
          TOTAL GENERAL: <span style={{ color: theme.colors.primary, marginLeft: 12 }}>{fmt(totalGeneral)}</span>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <Button
            size="small"
            danger
            loading={anulando}
            disabled={asientoContableId != null}
            onClick={handleAnularCompra}
            icon={<StopOutlined />}
            style={{ minWidth: 120, height: 28, fontSize: 12 }}
          >
            Anular
          </Button>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" onClick={() => navigate('/listar_compraalgodon')} style={{ minWidth: 120, height: 28, fontSize: 12 }}>
              Cancelar
            </Button>
            <Button
              type="primary"
              size="small"
              loading={guardando}
              onClick={handleGuardar}
              style={{ minWidth: 120, height: 28, fontSize: 12, backgroundColor: theme.colors.primary, borderColor: theme.colors.primary }}
            >
              Guardar Cambios
            </Button>
          </div>
        </div>
      </StyledCard>
    </div>
  );
};

export default EditarCompraAlgodon;
